import { Injectable } from '@angular/core';
import { extractErrorMessage } from 'src/app/service/admin-error';
import { APIService } from 'src/app/service/api.service';
import { failIfSwallowed } from 'src/app/util/swallowed-http';

export interface imageResizeOption {
  width?: number;
  height?: number;
  fit?: string;
}

type PresignedPostResponse = {
  url: string;
  fields: { [key: string]: string };
  uploadUrl: string;
  key: string;
  expiresSeconds: number;
};

const SWALLOWED_UPLOAD_ERROR = '일시적인 오류로 요청이 처리되지 않았습니다. 잠시 후 다시 시도해주세요.';

/** S3 POST 재시도. 업로드 flake(순간 망 끊김·S3 5xx)는 한 번 더 보내면 대개 붙는다. */
const S3_UPLOAD_MAX_ATTEMPTS = 2;
const S3_UPLOAD_RETRY_DELAY_MS = 800;

@Injectable({
  providedIn: 'root',
})
export class S3Service {
  constructor(private apiService: APIService) {}

  private generateRandomString(length: number = 6): string {
    return Math.random().toString(36).substr(2, length); // 랜덤 문자열 생성
  }

  private getCurrentTimestamp(): string {
    const now = new Date();
    return now
      .toISOString() // ISO 8601 포맷으로 변환
      .replace(/[-:.TZ]/g, '') // 불필요한 문자를 제거하여 `YYYYMMDDHHmmSSSSS` 형식으로 변환
      .slice(0, 17); // 초 단위까지만 사용
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async uploadFile(file: File, folder: string): Promise<{ key: string; url: string }> {
    const { postUrl, formData, resp } = await this.getPresignedPostData(file, folder);

    let uploadResp: Response | undefined;
    let fetchError: unknown;
    for (let attempt = 1; attempt <= S3_UPLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        uploadResp = await fetch(postUrl, {
          method: 'POST',
          body: formData,
        });
        fetchError = undefined;
        // 4xx(서명·권한)는 다시 보내도 같아서 즉시 실패로 끊는다.
        if (uploadResp.ok || (uploadResp.status >= 400 && uploadResp.status < 500)) break;
      } catch (error) {
        uploadResp = undefined;
        fetchError = error;
      }
      if (attempt < S3_UPLOAD_MAX_ATTEMPTS) await this.sleep(S3_UPLOAD_RETRY_DELAY_MS);
    }

    if (!uploadResp) {
      const detail = extractErrorMessage(fetchError, String(fetchError));
      throw new Error(
        `이미지 서버에서 응답을 받지 못했습니다. 네트워크 문제이거나 브라우저가 요청을 막은 경우입니다.` +
          (detail ? ` (${detail})` : ''),
        { cause: fetchError },
      );
    }

    if (!uploadResp.ok) {
      const body = await uploadResp.text().catch(() => '');
      const s3Code = body.match(/<Code>([^<]{1,80})<\/Code>/)?.[1];
      const statusText = uploadResp.statusText?.trim();
      throw new Error(
        `이미지 서버가 업로드를 거절했습니다: HTTP ${uploadResp.status}` +
          `${statusText ? ` ${statusText}` : ''}` +
          `${s3Code ? ` (${s3Code})` : ''}`,
      );
    }

    return { key: resp.key, url: resp.uploadUrl };
  }

  async getPresignedPostData(
    file: File,
    folder: string,
  ): Promise<{
    postUrl: string;
    formData: FormData;
    resp: {
      key: string;
      uploadUrl: string;
    };
  }> {
    const randomString = this.generateRandomString(6);
    // 다단계 folder(예: host_private/123) 사용 시 파일명에는 마지막 segment만 prefix로 둠
    const folderLeaf = folder.split('/').pop() || folder;
    const fileName = `${folderLeaf}-${this.getCurrentTimestamp()}-${randomString}.${file.name.split('.').pop()}`;

    let resp: PresignedPostResponse;
    try {
      resp = await this.apiService
        .post<PresignedPostResponse>(`/api/files/images/presigned-post`, {
          key: `${folder}/${fileName}`,
        })
        .pipe(failIfSwallowed(SWALLOWED_UPLOAD_ERROR))
        .toPromise();
      if (!resp?.url || !resp.fields || !resp.key || !resp.uploadUrl) {
        throw new Error('업로드 주소 응답이 비어 있습니다.');
      }
    } catch (error) {
      throw new Error(`이미지 업로드 주소를 발급하지 못했습니다. ${extractErrorMessage(error, String(error))}`, {
        cause: error,
      });
    }

    const postUrl = resp.url;
    const formData = new FormData();
    Object.entries(resp.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('Content-Type', file.type);
    formData.append('file', file, file.name);

    return { postUrl, formData, resp };
  }

  async resizeImage(
    key: string,
    prefix: string,
    option: imageResizeOption,
  ): Promise<{
    url: string;
  }> {
    try {
      const newKey = `${prefix}/${key.split('/')[1]}`;
      const resp = await this.apiService
        .post<{ url: string }>(`/api/files/images/resize`, {
          key,
          newKey,
          width: option.width,
          height: option.height,
          fit: option.fit,
        })
        .pipe(failIfSwallowed(SWALLOWED_UPLOAD_ERROR))
        .toPromise();
      if (!resp?.url) {
        throw new Error('크기 변환 응답이 비어 있습니다.');
      }
      return resp;
    } catch (error) {
      console.error('Image resize failed:', error);
      throw new Error(`이미지 크기를 변환하지 못했습니다. ${extractErrorMessage(error, String(error))}`, {
        cause: error,
      });
    }
  }
}
